import { z } from 'zod';
import { CognitoSubSchema, EmailSchema, TimestampSchema } from '../common.js';
/**
 * Backoffice action audit event — see docs/data-model.md §4.5.
 * Lives under `PK = AUDIT#<yyyy-mm>`, `SK = <iso>#<eventId>`.
 * GSI1 lets us query per-tenant: `GSI1PK = AUDIT-TENANT#<tenantId>`.
 */
export const AuditTargetTypeSchema = z.enum([
    'tenant',
    'user',
    'super-admin',
    'invite',
    'agent',
    'session',
]);
export const AuditResultSchema = z.enum(['success', 'failure']);
/**
 * Audit actor — discriminated on `group`:
 *
 *   - `super-admin`: backoffice-initiated action by an authenticated user.
 *     Carries the full Cognito sub + email.
 *   - tenant roles: dashboard-initiated action scoped to the actor's tenant.
 *   - `public`: unauthenticated end-user action surfaced through a public
 *     handler (e.g. session mint on an embedded agent). `userId` + `email`
 *     are null because we intentionally don't track the caller's identity;
 *     the IP + origin on `AuditRequestContext` are the available signals.
 *
 * Task P2-05 introduced the `public` variant so `withAudit` can wrap the
 * public agent-session-mint flow. Future public routes can reuse it.
 */
export const AuditActorSchema = z.discriminatedUnion('group', [
    z.object({
        group: z.literal('super-admin'),
        userId: CognitoSubSchema,
        email: EmailSchema,
    }),
    z.object({
        group: z.literal('tenant-admin'),
        userId: CognitoSubSchema,
        email: EmailSchema,
        tenantId: z.string().uuid(),
    }),
    z.object({
        group: z.literal('knowledge-manager'),
        userId: CognitoSubSchema,
        email: EmailSchema,
        tenantId: z.string().uuid(),
    }),
    z.object({
        group: z.literal('tenant-user'),
        userId: CognitoSubSchema,
        email: EmailSchema,
        tenantId: z.string().uuid(),
    }),
    z.object({
        group: z.literal('public'),
        userId: z.null(),
        email: z.null(),
    }),
]);
export const AuditTargetSchema = z.object({
    type: AuditTargetTypeSchema,
    id: z.string(),
    tenantId: z.string().uuid().optional(),
});
export const AuditRequestContextSchema = z.object({
    ip: z.string(),
    userAgent: z.string(),
});
export const AuditLogEventSchema = z.object({
    eventId: z.string().uuid(),
    timestamp: TimestampSchema,
    actor: AuditActorSchema,
    /** Dotted action name, e.g. "tenant.create", "user.delete", "super-admin.invite". */
    action: z.string().min(1).max(80),
    target: AuditTargetSchema,
    request: AuditRequestContextSchema,
    /** Action-specific payload; keep < 2 KB (DDB row size matters). */
    details: z.record(z.unknown()),
    result: AuditResultSchema,
    errorCode: z.string().optional(),
});
//# sourceMappingURL=auditLogEvent.js.map